import { Box, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { MarketDialogueAnswerType, type AnsweredQuestionDto, type MarketDialogueQuestionDto } from "../../autogen/bff-api";
import { AttachmentRow } from "../../common/documents/AttachmentRow";
import { DocumentsAsOwnerTable } from "../../common/documents/DocumentsAsOwnerTable";
import { isTiptapContentEmpty, TipTap } from "../../common/input/TipTap/TipTap";
import { BusinessSecretNote } from "./BusinessSecretNote";
import { answerTypeOf } from "./marketDialogueLabels";

export const GivenAnswer = ({
  question,
  given,
  maxHeight = "",
  compact = false,
}: {
  question: MarketDialogueQuestionDto;
  given: AnsweredQuestionDto | undefined;
  maxHeight?: string;
  compact?: boolean;
}) => {
  const { t } = useTranslation();
  const documents = given?.documents ?? [];
  const text = given?.text?.trim() ?? "";
  const hasText = !isTiptapContentEmpty(text);
  const choice = given?.choice ?? null;
  const withheld = given?.withheld ?? false;
  const answered = hasText || choice !== null || documents.length > 0 || withheld;
  return (
    <>
      {!answered && (
        <Text color="smMuted" mt="1">
          {t("Not answered")}
        </Text>
      )}
      {choice !== null && (
        <Text mt="1" fontWeight="semibold">
          {choice ? t("Yes") : t("No")}
        </Text>
      )}
      {hasText &&
        (answerTypeOf(question) === MarketDialogueAnswerType.FreeText ? (
          <Box mt="1">
            <TipTap content={text} editable={false} hasBorder={false} padding="0" maxHeight={maxHeight} />
          </Box>
        ) : (
          <Text whiteSpace="pre-wrap" mt="1">
            {text}
          </Text>
        ))}
      {withheld && (
        <BusinessSecretNote
          label={t("Part of this answer is marked as a business secret")}
          reason={t("Only the market dialogue team can read it.")}
        />
      )}
      {documents.length > 0 && (
        <Box mt="2">
          {compact ? (
            <Stack spacing="1" data-testid="compact-documents">
              {documents.map((document) => (
                <AttachmentRow key={document.id} attachment={{ id: document.id, fileName: document.name }} />
              ))}
            </Stack>
          ) : (
            <DocumentsAsOwnerTable documents={documents} />
          )}
        </Box>
      )}
      {given?.secretReason && (
        <BusinessSecretNote label={t("The answer is marked as a business secret")} reason={given.secretReason} />
      )}
      {(given?.secretDocuments ?? []).map((secret) => (
        <BusinessSecretNote
          key={secret.documentId}
          label={t("«{{name}}» is marked as a business secret", {
            name: documents.find((d) => d.id === secret.documentId)?.name ?? "",
          })}
          reason={secret.reason}
        />
      ))}
    </>
  );
};
